import { DataDisplay } from "@certn/cascadia-react";
import { Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import { t } from "i18next";
import type { ReactNode } from "react";

import {
  buildTaxRows,
  getCurrencySymbol,
  isNotZero,
  numberFormatter,
  segmentHasNonZeroLineTax,
} from "./utils";
import { InvoiceProps } from "../../../../src/schemas-and-types";

type SegmentsProps = {
  invoiceData: InvoiceProps["invoiceData"];
  segmentKey: string;
  isClient: boolean;
  showSubtotal: boolean;
};

function summaryLabelCell(showTax: boolean, label: ReactNode) {
  return showTax ? { tax: label } : { itemPrice: label };
}

function buildRows(
  segmentKey: string,
  segment: SegmentsProps["invoiceData"]["segments"][string],
  currency: SegmentsProps["invoiceData"]["currency"],
  isClient: boolean,
  showTax: boolean,
  showSubtotal: boolean
) {
  const currencySymbol = getCurrencySymbol(currency);

  const lineItems = Object.entries(segment.line_items).map(
    ([productCode, item]) => {
      return {
        key: `${segmentKey}:${productCode}`,
        item:
          item.type === "CREDENTIAL"
            ? `${t("Credential Accepted")}: ${item.title}`
            : `${item.title}`,
        quantity: `${item.quantity}`,
        itemPrice: item.unit_price_amount
          ? `${currencySymbol}${numberFormatter(item.unit_price_amount)}`
          : null,
        ...(showTax
          ? {
              tax:
                item.tax_amount != null
                  ? `${currencySymbol}${numberFormatter(item.tax_amount)}`
                  : null,
            }
          : {}),
        lineAmount: `${currencySymbol}${numberFormatter(item.extended_price_amount)}`,
      };
    }
  );

  const creditTotal = segment.credit_total;
  const hasCredits = !!creditTotal && isNotZero(creditTotal);
  const refundTotal = segment.refund_total;
  const hasRefunds = !!refundTotal && isNotZero(refundTotal);

  const groupGrossTotal = hasCredits
    ? String(parseFloat(segment.total) - parseFloat(creditTotal))
    : null;

  const refundRows = hasRefunds
    ? [
        {
          key: `${segmentKey}:summary:refund_total`,
          ...summaryLabelCell(
            showTax,
            <Text style="global/button-medium">{t("Refunded Amount")}</Text>
          ),
          lineAmount: (
            <Text style="global/button-medium">
              {currencySymbol} {numberFormatter(refundTotal)}
            </Text>
          ),
        },
      ]
    : [];

  const segmentSummaryRows = [
    ...(showSubtotal
      ? [
          {
            key: `${segmentKey}:summary:subtotal`,
            ...summaryLabelCell(
              showTax,
              <Text style="global/button-medium">{t("Subtotal")}</Text>
            ),
            lineAmount: (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(segment.subtotal)}
              </Text>
            ),
          },
        ]
      : []),
    ...buildTaxRows(
      segment.taxes,
      currencySymbol,
      segmentKey,
      showTax ? "tax" : "itemPrice"
    ),
    ...(hasCredits
      ? [
          {
            key: `${segmentKey}:summary:gross_total`,
            ...summaryLabelCell(
              showTax,
              <Text style="global/button-medium">{t("Group Total")}</Text>
            ),
            lineAmount: groupGrossTotal ? (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(groupGrossTotal)}
              </Text>
            ) : null,
          },
          {
            key: `${segmentKey}:summary:credit_total`,
            ...summaryLabelCell(
              showTax,
              <Text style="global/button-medium">{t("Credits")}</Text>
            ),
            lineAmount: (
              <Text style="global/button-medium">
                {currencySymbol} {numberFormatter(creditTotal)}
              </Text>
            ),
          },
          ...refundRows,
          {
            key: `${segmentKey}:summary:total`,
            ...summaryLabelCell(
              showTax,
              <Text style="global/button-medium">{t("Group Amount Due")}</Text>
            ),
            lineAmount: (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(segment.total)}
              </Text>
            ),
          },
        ]
      : [
          ...refundRows,
          {
            key: `${segmentKey}:summary:total`,
            ...summaryLabelCell(
              showTax,
              <Text style="global/button-medium">{t("Group Total")}</Text>
            ),
            lineAmount: (
              <Text style="global/button-medium">
                {currencySymbol}
                {numberFormatter(segment.total)}
              </Text>
            ),
          },
        ]),
  ];

  return isClient ? [...lineItems, ...segmentSummaryRows] : lineItems;
}

export function Segment(props: SegmentsProps) {
  const { invoiceData, segmentKey, isClient, showSubtotal } = props;
  const segment = invoiceData.segments[segmentKey];
  const showTax = segmentHasNonZeroLineTax(segment);

  const rows = buildRows(
    segmentKey,
    segment,
    invoiceData.currency,
    isClient,
    showTax,
    showSubtotal
  );

  return (
    <section>
      {isClient && (
        <Text style="global/heading-large">{segment.customer_group_name}</Text>
      )}
      <DataDisplay
        className={styles.table}
        testId="invoice-line-items"
        aria-label={t("Invoice Line Items")}
        stickyHeader
        renderTopMargin={false}
        columns={[
          { key: "item", name: t("Item"), isRowHeader: true },
          { key: "quantity", name: t("Quantity") },
          { key: "itemPrice", name: t("Item Price") },
          ...(showTax ? [{ key: "tax", name: t("Tax") }] : []),
          { key: "lineAmount", name: t("Line Amount") },
        ]}
        rows={rows}
      />
    </section>
  );
}

const styles = {
  table: css({
    // Column widths
    "& th[data-key='item']": {
      width: "auto",
    },
    "& th[data-key='quantity']": {
      width: "6em",
    },
    "& th[data-key='itemPrice'], & th[data-key='tax']": {
      width: "8em",
    },
    "& th[data-key='lineAmount']": {
      width: "9em",
    },
    // Horizontal alignment of header and data cells
    "& th[data-key='quantity'], & td[data-key$='quantity']": {
      textAlign: "center",
    },
    "& th[data-key='lineAmount'], & td[data-key$='lineAmount']": {
      textAlign: "right",
    },
    "& th[data-key='itemPrice'], & td[data-key$='itemPrice']": {
      textAlign: "right",
    },
    "& th[data-key='tax'], & td[data-key$='tax']": {
      textAlign: "right",
    },
    // Summary sections
    "& tr[data-key*=':summary:']": {
      // Don't allow page breaking within summary rows
      pageBreakBefore: "avoid",
      // No borders between summary rows
      borderBottomWidth: 0,
      td: {
        // Less padding for summary rows
        paddingTop: 2,
        paddingBottom: 2,
      },
    },
  }),
};
